// Страница управления пользователями (только администраторы): список пользователей с возможностью смены ролей.

import { useEffect, useState } from 'react';
import {
  Container,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  Modal,
} from 'react-bootstrap';
import useAuthStore from '../../store/authStore';
import useUserStore from '../../store/userStore';
import AppNavbar from '../../components/common/AppNavbar';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

const ROLE_VARIANT = { admin: 'danger', teacher: 'warning', student: 'secondary' };
const ROLE_LABEL = { admin: 'Админ', teacher: 'Преподаватель', student: 'Студент' };
const ALL_ROLES = ['student', 'teacher', 'admin'];

/** Инициалы из имени */
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function UsersPage() {
  const { fetchMe } = useAuthStore();
  const { users, fetchUsers, assignRole, removeRole, deactivateUser, resetPassword: resetUserPassword } = useUserStore();

  const [roleModal, setRoleModal] = useState(null);
  const [modalMode, setModalMode] = useState('assign');
  const [selectedRole, setSelectedRole] = useState('teacher');
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [deactivatingId, setDeactivatingId] = useState(null);

  // Состояние модала сброса пароля
  const [resetModal, setResetModal] = useState(null); // объект пользователя или null
  const [resetPassword, setResetPasswordValue] = useState('');
  const [resetPasswordShow, setResetPasswordShow] = useState(false);
  const [savingReset, setSavingReset] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    Promise.all([fetchMe(), fetchUsers()]);
  }, []);

  const openRoleModal = (user, mode) => {
    setRoleModal(user);
    setModalMode(mode);
    setSelectedRole(mode === 'remove' ? (user.roles[0] ?? 'student') : 'teacher');
    setRoleError('');
  };

  const handleRoleAction = async () => {
    setSavingRole(true);
    setRoleError('');
    try {
      if (modalMode === 'assign') {
        await assignRole(roleModal.id, selectedRole);
      } else {
        await removeRole(roleModal.id, selectedRole);
      }
      setRoleModal(null);
    } catch (err) {
      setRoleError(err?.response?.data?.detail ?? 'Ошибка при изменении роли');
    } finally {
      setSavingRole(false);
    }
  };

  const openResetModal = (user) => {
    setResetModal(user);
    setResetPasswordValue('');
    setResetPasswordShow(false);
    setResetError('');
    setResetSuccess(false);
  };

  const handleResetPassword = async () => {
    if (resetPassword.length < 6) {
      setResetError('Пароль должен содержать минимум 6 символов');
      return;
    }
    setSavingReset(true);
    setResetError('');
    try {
      await resetUserPassword(resetModal.id, resetPassword);
      setResetSuccess(true);
      setResetPasswordValue('');
    } catch (err) {
      setResetError(err?.response?.data?.detail ?? 'Ошибка при сбросе пароля');
    } finally {
      setSavingReset(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Деактивировать пользователя? Он не сможет войти в систему.')) return;
    setDeactivatingId(userId);
    try {
      await deactivateUser(userId);
    } catch (err) {
      alert(err?.response?.data?.detail ?? 'Ошибка при деактивации');
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">
          {/* ── Заголовок ── */}
          <div
            className="d-flex justify-content-between align-items-center mb-4 pb-3"
            style={{ borderBottom: '1px solid #e9ecef' }}
          >
            <div>
              <h5 className="fw-bold mb-0" style={{ color: '#1e2a4a', letterSpacing: '-0.01em' }}>
                Пользователи
              </h5>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem' }}>
                {users.length > 0
                  ? `${users.length} ${
                      users.length === 1
                        ? 'пользователь'
                        : users.length < 5
                        ? 'пользователя'
                        : 'пользователей'
                    }`
                  : 'Нет пользователей'}
              </p>
            </div>
          </div>

          {/* ── Таблица ── */}
          {users.length === 0 ? (
            <div
              className="text-center py-5"
              style={{
                background: '#fff',
                borderRadius: '0.875rem',
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
              <h6 className="fw-semibold text-muted mb-1">Пользователей нет</h6>
            </div>
          ) : (
            <div className="table-wrapper">
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '3rem' }}>#</th>
                    <th>Пользователь</th>
                    <th>Email</th>
                    <th>Роли</th>
                    <th style={{ width: '8rem' }}>Статус</th>
                    <th style={{ width: '16rem' }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.id}>
                      <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                        {idx + 1}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {/* Аватар-инициалы */}
                          <div
                            className="ewl-avatar"
                            style={{
                              opacity: u.active ? 1 : 0.45,
                            }}
                          >
                            {getInitials(u.name)}
                          </div>
                          <span
                            className="fw-medium"
                            style={{ color: u.active ? '#1e2a4a' : '#adb5bd' }}
                          >
                            {u.name}
                          </span>
                        </div>
                      </td>
                      <td
                        className="text-muted"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {u.email}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {u.roles.map((r) => (
                            <Badge
                              key={r}
                              bg={ROLE_VARIANT[r] ?? 'secondary'}
                              style={{ fontSize: '0.7rem', fontWeight: 600 }}
                            >
                              {ROLE_LABEL[r] ?? r}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td>
                        <Badge
                          bg={u.active ? 'success' : 'secondary'}
                          style={{ fontSize: '0.7rem', fontWeight: 600 }}
                        >
                          {u.active ? 'Активен' : 'Неактивен'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => openRoleModal(u, 'assign')}
                          style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8rem' }}
                        >
                          Назначить роль
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-warning"
                          className="me-2"
                          disabled={u.roles.length <= 1}
                          onClick={() => openRoleModal(u, 'remove')}
                          style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8rem' }}
                        >
                          Удалить роль
                        </Button>
                        {u.active && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="me-2"
                            disabled={deactivatingId === u.id}
                            onClick={() => handleDeactivate(u.id)}
                            style={{ borderRadius: '0.4rem', fontSize: '0.8rem' }}
                          >
                            {deactivatingId === u.id ? (
                              <Spinner size="sm" animation="border" />
                            ) : (
                              'Деактивировать'
                            )}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openResetModal(u)}
                          style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8rem' }}
                        >
                          Сбросить пароль
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Container>
      </div>

      {/* ── Модальное окно сброса пароля ── */}
      <Modal show={Boolean(resetModal)} onHide={() => setResetModal(null)}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Сбросить пароль
            </Modal.Title>
            <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
              {resetModal?.name}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {resetError && (
            <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              {resetError}
            </Alert>
          )}
          {resetSuccess && (
            <Alert variant="success" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              Пароль успешно сброшен
            </Alert>
          )}

          <Form.Group className="mb-2">
            <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
              Новый пароль
            </Form.Label>
            <div className="position-relative">
              <Form.Control
                type={resetPasswordShow ? 'text' : 'password'}
                placeholder="Минимум 6 символов"
                value={resetPassword}
                onChange={(e) => setResetPasswordValue(e.target.value)}
                style={{ borderRadius: '0.4rem', paddingRight: '2.5rem' }}
                disabled={resetSuccess}
              />
              <button
                type="button"
                onClick={() => setResetPasswordShow((v) => !v)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#6c757d',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
                tabIndex={-1}
              >
                {resetPasswordShow ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Form.Group>

          <Form.Text className="text-muted" style={{ fontSize: '0.8125rem' }}>
            Пользователь должен будет использовать новый пароль при следующем входе.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setResetModal(null)}>
            {resetSuccess ? 'Закрыть' : 'Отмена'}
          </Button>
          {!resetSuccess && (
            <Button
              variant="dark"
              onClick={handleResetPassword}
              disabled={savingReset || !resetPassword}
              style={{ fontWeight: 500 }}
            >
              {savingReset ? <Spinner size="sm" animation="border" /> : 'Сбросить пароль'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* ── Модальное окно назначения роли ── */}
      <Modal show={Boolean(roleModal)} onHide={() => setRoleModal(null)}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {modalMode === 'assign' ? 'Назначить роль' : 'Удалить роль'}
            </Modal.Title>
            <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
              {roleModal?.name}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4">
          {roleError && (
            <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
              {roleError}
            </Alert>
          )}

          <Form.Group className="mb-2">
            <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
              Роль
            </Form.Label>
            <Form.Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              {(modalMode === 'remove' ? (roleModal?.roles ?? []) : ALL_ROLES).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r] ?? r}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Text className="text-muted" style={{ fontSize: '0.8125rem' }}>
            {modalMode === 'assign'
              ? 'Роль будет добавлена к уже имеющимся ролям пользователя.'
              : 'Выбранная роль будет удалена у пользователя.'}
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRoleModal(null)}>
            Отмена
          </Button>
          <Button
            variant={modalMode === 'assign' ? 'primary' : 'warning'}
            onClick={handleRoleAction}
            disabled={savingRole}
            style={{ fontWeight: 500 }}
          >
            {savingRole ? <Spinner size="sm" animation="border" /> : (modalMode === 'assign' ? 'Назначить' : 'Удалить')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
